/**
 * Shared inline-style building blocks, built on @edufadder/ui's design
 * tokens so the web app visually matches the extension's in-page card and
 * the original mockups (same palette, same serif/sans split) without
 * pulling in a CSS framework. See packages/ui/src/tokens.ts for the
 * canonical values.
 */
import { colors, fonts, radii, spacing } from "@edufadder/ui";
import type { CSSProperties } from "react";

export const page: CSSProperties = {
  minHeight: "100vh",
  background: colors.background,
  color: colors.ink,
  fontFamily: fonts.sans,
};

export const container: CSSProperties = {
  maxWidth: 880,
  margin: "0 auto",
  padding: `${spacing(6)} ${spacing(4)}`,
};

export const heading1: CSSProperties = {
  fontFamily: fonts.serif,
  fontSize: 28,
  margin: `0 0 ${spacing(2)} 0`,
};

export const heading2: CSSProperties = {
  fontFamily: fonts.serif,
  fontSize: 20,
  margin: `0 0 ${spacing(2)} 0`,
};

export const card: CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radii.md,
  padding: spacing(4),
  marginBottom: spacing(4),
};

export const label: CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  marginBottom: spacing(1),
};

export const input: CSSProperties = {
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  padding: `${spacing(2)} ${spacing(2)}`,
  border: `1px solid ${colors.border}`,
  borderRadius: radii.sm,
  fontSize: 14,
  marginBottom: spacing(3),
};

export const textarea: CSSProperties = { ...input, minHeight: 80, fontFamily: fonts.sans, resize: "vertical" };

export const buttonPrimary: CSSProperties = {
  padding: `${spacing(2)} ${spacing(4)}`,
  background: colors.brand,
  color: "#fff",
  border: "none",
  borderRadius: radii.sm,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

export const buttonSecondary: CSSProperties = {
  padding: `${spacing(2)} ${spacing(4)}`,
  background: colors.surface,
  color: colors.ink,
  border: `1px solid ${colors.border}`,
  borderRadius: radii.sm,
  fontSize: 14,
  cursor: "pointer",
};

export const buttonDanger: CSSProperties = { ...buttonSecondary, color: colors.dangerRed, borderColor: colors.dangerRed };

export const mutedText: CSSProperties = { color: colors.muted, fontSize: 13 };

export const errorText: CSSProperties = { color: colors.dangerRed, fontSize: 13, marginBottom: spacing(2) };

export const nav: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: `${spacing(3)} ${spacing(4)}`,
  borderBottom: `1px solid ${colors.border}`,
  background: colors.surface,
};

export const navLinks: CSSProperties = { display: "flex", gap: spacing(4), alignItems: "center" };

export const table: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
export const th: CSSProperties = { textAlign: "left", padding: spacing(2), borderBottom: `2px solid ${colors.border}`, color: colors.muted };
export const td: CSSProperties = { padding: spacing(2), borderBottom: `1px solid ${colors.border}` };

// ---------------------------------------------------------------------------
// Site shell (header/footer) and public-page building blocks.
// ---------------------------------------------------------------------------

export const headerInner: CSSProperties = {
  maxWidth: 1120,
  margin: "0 auto",
  padding: `${spacing(3)} ${spacing(4)}`,
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: spacing(6),
};

/** Visibility is controlled by .ef-nav in global.css (media queries), so no `display` here. */
export const navBar: CSSProperties = {};

export const menuToggle: CSSProperties = {
  marginLeft: "auto",
  background: "transparent",
  color: "#fff",
  border: `1px solid ${colors.gold}`,
  borderRadius: radii.sm,
  padding: `${spacing(1)} ${spacing(3)}`,
  fontSize: 14,
  cursor: "pointer",
};

export const skipLink: CSSProperties = {
  position: "absolute",
  left: -9999,
  top: 0,
};

export const buttonOnDark: CSSProperties = {
  padding: `${spacing(2)} ${spacing(3)}`,
  background: "transparent",
  color: "#fff",
  border: `1px solid ${colors.gold}`,
  borderRadius: radii.sm,
  fontSize: 14,
  cursor: "pointer",
};

/** Gold call-to-action: dark ink text on FSU Gold (9.2:1). */
export const buttonGold: CSSProperties = {
  display: "inline-block",
  padding: `${spacing(2)} ${spacing(4)}`,
  background: colors.gold,
  color: colors.ink,
  border: "none",
  borderRadius: radii.sm,
  fontSize: 14,
  fontWeight: 700,
  textDecoration: "none",
  cursor: "pointer",
};

export const buttonPrimaryLink: CSSProperties = {
  ...buttonPrimary,
  display: "inline-block",
  textDecoration: "none",
  color: "#fff",
};

export const buttonSecondaryLink: CSSProperties = {
  ...buttonSecondary,
  display: "inline-block",
  textDecoration: "none",
};

export const footerLink: CSSProperties = { color: "#e8e6df", textDecoration: "none" };

export const wide: CSSProperties = {
  maxWidth: 1120,
  margin: "0 auto",
  padding: `${spacing(8)} ${spacing(4)}`,
};

export const lead: CSSProperties = { fontSize: 18, lineHeight: 1.6, color: colors.ink };

export const prose: CSSProperties = { fontSize: 16, lineHeight: 1.7 };

export const sectionTitle: CSSProperties = {
  fontFamily: fonts.serif,
  fontSize: 24,
  color: colors.brand,
  margin: `${spacing(8)} 0 ${spacing(3)} 0`,
};

export const goldRule: CSSProperties = {
  width: 56,
  height: 4,
  background: colors.gold,
  border: "none",
  borderRadius: 2,
  margin: `0 0 ${spacing(4)} 0`,
};

export const chip: CSSProperties = {
  display: "inline-block",
  padding: `2px ${spacing(2)}`,
  borderRadius: radii.pill,
  fontSize: 12,
  fontWeight: 600,
  background: colors.neutralChipBg,
  color: colors.ink,
};

export const statNumber: CSSProperties = { fontFamily: fonts.serif, fontSize: 32, fontWeight: 700, color: colors.brand, margin: 0 };
export const statLabel: CSSProperties = { fontSize: 13, color: colors.muted, margin: 0 };
